/**
 * RestoID — toasts via sonner, styled to the Claude Design.
 * ---------------------------------------------------------------------------
 * Drop-in replacement for the upstream ToastProvider (swapped in at
 * src/Providers/AppBoundary). Logto raises notifications through a single
 * one-shot string in PageContext (`setToast`); we bridge that string into a
 * styled sonner toast and render <Toaster>. Most upstream `setToast(...)` calls are
 * errors (red ✕); the copy confirmations are detected by `toneOf` and shown as the
 * success variant.
 */
import { type ReactNode, useContext, useEffect, useRef } from 'react';
import { Toaster, toast } from 'sonner';
import 'sonner/dist/styles.css';

import PageContext from '@/Providers/PageContextProvider/PageContext';

import { C, FONT, Icon } from './restoid';

type Tone = 'error' | 'success';

/** Most upstream setToast() calls are errors; the copy confirmations are successes
 *  (e.g. "Резервный код скопирован." / "Secret key copied."). Kept narrow to "copied"
 *  so it can't misfire on an error like "не удалось сохранить". */
const toneOf = (message: string): Tone =>
  /скопирован|copied/i.test(message) ? 'success' : 'error';

const TONES: Record<Tone, { bg: string; border: string; color: string; icon: string }> = {
  error: {
    bg: 'var(--rid-toast-danger-tile, rgba(241,112,122,0.16))',
    border: 'var(--rid-toast-danger-tile-line, rgba(241,112,122,0.42))',
    color: C.danger,
    icon: 'x',
  },
  success: {
    // The toast green is its own token (--rid-toast-success), NOT --rid-success:
    // the card carries the design's brighter confirmation green.
    bg: 'var(--rid-toast-success-tile, rgba(98,197,84,0.16))',
    border: 'var(--rid-toast-success-tile-line, rgba(98,197,84,0.42))',
    color: 'var(--rid-toast-success, #62c554)',
    icon: 'check',
  },
};

/** One toast card — the Claude Design "Sonner-style" notification. */
const RestoToast = ({
  id,
  message,
  tone,
}: {
  id: string | number;
  message: string;
  tone: Tone;
}) => {
  const t = TONES[tone];
  return (
    // Auto-dismisses in 5s and is announced via role="alert"; click-to-dismiss is a
    // pointer-only convenience, so keyboard/AT users are never stranded by it.
    // eslint-disable-next-line jsx-a11y/click-events-have-key-events, jsx-a11y/no-noninteractive-element-interactions
    <div
      role="alert"
      onClick={() => toast.dismiss(id)}
      style={{
        pointerEvents: 'auto',
        cursor: 'pointer',
        // Hug the content so a short message stays compact, capped at the container
        // width. The li (forced to --width) flex-centres this card.
        width: 'fit-content',
        maxWidth: 'min(92vw, 400px)',
        display: 'flex',
        alignItems: 'flex-start',
        gap: 12,
        padding: '14px 16px',
        borderRadius: 14,
        background: 'var(--rid-toast-card, rgba(22,24,28,0.93))',
        backdropFilter: 'blur(16px)',
        WebkitBackdropFilter: 'blur(16px)',
        border: `1px solid ${C.line}`,
        boxShadow:
          'var(--rid-shadow-toast, 0 14px 40px -12px rgba(0,0,0,0.65)), inset 0 1px 0 var(--rid-inset-toast, rgba(255,255,255,0.05))',
      }}
    >
      <span
        style={{
          flex: '0 0 auto',
          width: 21,
          height: 21,
          borderRadius: '50%',
          background: t.bg,
          border: `1px solid ${t.border}`,
          display: 'grid',
          placeItems: 'center',
          color: t.color,
        }}
      >
        <Icon name={t.icon} size={12} sw={2.6} />
      </span>
      <span
        style={{
          // Not flex:1 — the card hugs its content; the message takes its natural
          // width and only shrinks/wraps when the card hits its max-width.
          flex: '0 1 auto',
          minWidth: 0,
          paddingTop: 1,
          color: C.text,
          font: `600 14px/1.45 ${FONT}`,
        }}
      >
        {message}
      </span>
    </div>
  );
};

const RestoToastProvider = ({ children }: { children: ReactNode }) => {
  const { toast: message, setToast } = useContext(PageContext);
  const lastRef = useRef('');

  // Bridge the upstream one-shot PageContext string into a sonner toast, then
  // consume it (mirrors the upstream provider's hideToast).
  useEffect(() => {
    if (!message) {
      lastRef.current = '';
      return;
    }
    if (message === lastRef.current) {
      return;
    }

    lastRef.current = message;
    const tone = toneOf(message);
    toast.custom((id) => <RestoToast id={id} message={message} tone={tone} />, { duration: 5000 });
    setToast('');
  }, [message, setToast]);

  return (
    <>
      {children}
      <Toaster
        position="top-center"
        // Clears the fixed language/theme cluster in the page chrome, which ends at
        // ~52px (22px header padding + a ~30px pill). At the old offset of 20 the
        // toast landed exactly on top of it and, on mobile, hid it completely —
        // the toast is full-width there, so the two cannot sit side by side.
        //
        // The TOP only, as an object. A number sets all four sides, and below 600px
        // sonner lays the toaster out full-width from its LEFT offset: 72 on every side
        // put the toast 72px from the left edge and ran it off the right one (seen on
        // an iPhone, 2026-09-29). Left and right keep sonner's 16px.
        offset={{ top: 72 }}
        // Sonner switches to a SEPARATE offset below ~600px wide, so setting only
        // `offset` leaves mobile at its 16px default — which is exactly where the
        // chrome sits, and on mobile the toast is full-width so it hides it entirely.
        mobileOffset={{ top: 72 }}
        gap={10}
        // The li is forced to the full --width (restoid.css) and flex-centres the
        // content-hugging card inside it, so short messages stay compact AND centred;
        // --width itself is set on [data-sonner-toaster] in restoid.css.
        toastOptions={{ unstyled: true, className: 'rt-toast-li' }}
      />
    </>
  );
};

export default RestoToastProvider;
